import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/react-hooks'
import * as Mutation from '../../../api/gql/mutation'
import { MAX_DURATION_DAYS, type ColumnId, MAX_OWNER_LENGTH } from '../lib/constants'
import { isIsoDate } from '../lib/format'
import { diffPredecessors, parsePredecessors, type LocalLink } from '../lib/predecessors'
import type { Tree, VisibleRow } from '../lib/tree'
import { useGanttStore, useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleActivity } from '../types'
import type { CellMove } from '../detail/cells/CellEditor'
import { useScheduleMutations } from './useScheduleMutations'

/** Columns wired for inline editing (spec B §9.3 / §9.4). */
const EDITABLE_COLUMNS: ColumnId[] = ['name', 'owner', 'duration', 'start', 'finish', 'predecessors', 'percent']
/** Summaries are read-only for everything but `name` (spec A); this also makes a summary's
 * `predecessors` cell read-only, matching the server's rule that a summary cannot be a successor. */
const isEditableColumn = (a: ScheduleActivity, column: ColumnId): boolean =>
  EDITABLE_COLUMNS.includes(column) && (!a.isSummary || column === 'name' || column === 'owner')

interface Deps {
  scheduleId: string
  byId: Map<string, ScheduleActivity>
  canEdit: boolean
  columns: ColumnId[]
  rows: VisibleRow<ScheduleActivity>[]
  rowIndexById: Map<string, number>
  visibleOrder: string[]
  /** The single mutation queue for the whole detail page — hoisted into `GanttView` (Task 16) so
   * row operations and cell edits share it instead of each owning a separate `useScheduleMutations`. */
  run: ReturnType<typeof useScheduleMutations>['run']
  tree: Tree<ScheduleActivity>
  predecessorsByActivity: Map<string, { local: LocalLink[] }>
  /** Task 3 (keyboard row entry): called after a Shift+Enter/Insert commit so the next row gets
   * created below the one just edited. Optional/undefined when the viewer cannot create activities. */
  insertAfter?: (id: string) => void
}

/**
 * Inline cell editing (spec B §9.3, Task 14): owns the pending map, the two mutations, focus
 * movement after a commit, and `startEdit` — the entry point both double-click and the Enter/F2
 * keyboard shortcut call into. Extracted out of `GanttView` once it crossed ~400 lines.
 */
export const useCellEditing = ({ scheduleId, byId, canEdit, columns, rows, rowIndexById, visibleOrder, run, tree, predecessorsByActivity, insertAfter }: Deps) => {
  const api = useGanttStoreApi()
  const editingCell = useGanttStore((s) => s.editingCell)
  const [updateActivities] = useMutation(Mutation.UPDATE_SCHEDULE_ACTIVITIES)
  const [setProgress] = useMutation(Mutation.SET_SCHEDULE_ACTIVITY_PROGRESS)
  const [createDependency] = useMutation(Mutation.CREATE_SCHEDULE_DEPENDENCY)
  const [updateDependency] = useMutation(Mutation.UPDATE_SCHEDULE_DEPENDENCY)
  const [deleteDependency] = useMutation(Mutation.DELETE_SCHEDULE_DEPENDENCY)
  const [pending, setPending] = useState<Map<string, { column: ColumnId; value: string }>>(() => new Map())

  const startEdit = useCallback((id: string, column: ColumnId) => {
    if (!canEdit) return
    const a = byId.get(id)
    if (!a || !isEditableColumn(a, column)) return
    api.getState().setEditingCell({ activityId: id, column })
  }, [api, canEdit, byId])

  const editableColumnsFor = useCallback((a: ScheduleActivity): ColumnId[] =>
    columns.filter((c) => isEditableColumn(a, c)),
  [columns])

  /** Task 3 (keyboard row entry): `Tab` on a focused row with no editor open jumps straight into
   * its first/last editable cell — same editability rules as `startEdit`/`editableColumnsFor`
   * (respects `canEdit` and the summary rule). No-op when the row has no editable column. */
  const startEditEdge = useCallback((id: string, edge: 'first' | 'last') => {
    if (!canEdit) return
    const a = byId.get(id)
    if (!a) return
    const cols = editableColumnsFor(a)
    if (!cols.length) return
    api.getState().setEditingCell({ activityId: id, column: edge === 'first' ? cols[0] : cols[cols.length - 1] })
  }, [api, canEdit, byId, editableColumnsFor])

  const moveFocusAfterCommit = useCallback((id: string, column: ColumnId, move: CellMove) => {
    const st = api.getState()
    // 'insert' is handled by `onCommitCell` before this ever runs; treated like 'stay' here only
    // as a defensive fallback.
    if (move === 'stay' || move === 'insert') { st.setEditingCell(null); return }
    const rowIndex = rowIndexById.get(id)
    if (rowIndex == null) { st.setEditingCell(null); return }
    if (move === 'down') {
      const next = rows[rowIndex + 1]
      if (!next) { st.setEditingCell(null); return }
      const nextId = next.node.activity.id
      st.select(nextId, 'replace', visibleOrder)
      st.setEditingCell({ activityId: nextId, column })
      return
    }
    // 'right' / 'left': the next editable column on this row, wrapping into the next/previous row
    // when this row has none left.
    const dir = move === 'right' ? 1 : -1
    let ri = rowIndex
    let cols = editableColumnsFor(rows[ri].node.activity)
    let ci = cols.indexOf(column)
    for (;;) {
      ci += dir
      if (ci >= 0 && ci < cols.length) {
        const targetId = rows[ri].node.activity.id
        if (ri !== rowIndex) st.select(targetId, 'replace', visibleOrder)
        st.setEditingCell({ activityId: targetId, column: cols[ci] })
        return
      }
      ri += dir
      if (ri < 0 || ri >= rows.length) { st.setEditingCell(null); return }
      cols = editableColumnsFor(rows[ri].node.activity)
      ci = dir === 1 ? -1 : cols.length
    }
  }, [api, rows, rowIndexById, visibleOrder, editableColumnsFor])

  // `commitCell` implements the spec B §9.3 validation/mutation table. `raw` has already passed
  // the editor's own synchronous validation; the checks here are the business rules a client-side
  // format check can't express (no-op values, summaries, the milestone<->task duration flip).
  const commitCell = useCallback(async (id: string, column: ColumnId, raw: string) => {
    const a = byId.get(id)
    if (!a) return
    const v = raw.trim()
    // Apollo's mutate functions resolve an `ExecutionResult` (`data` optional); `run` wants a
    // plain `{ data: T }`, so the cast just narrows the shape — the value at runtime is identical.
    let job: (() => Promise<{ data: any }>) | null = null
    const upd = (patch: Record<string, any>) => () => updateActivities({ variables: { scheduleId, patches: [{ id, ...patch }] } }) as unknown as Promise<{ data: any }>
    switch (column) {
      case 'name':
        if (!v || v.length > 500 || v === a.name) return
        job = upd({ name: v })
        break
      case 'owner': {
        const next = v.trim() || null
        if ((next && next.length > MAX_OWNER_LENGTH) || next === (a.owner ?? null)) return
        job = upd({ owner: next })
        break
      }
      case 'duration': {
        const n = Number(v)
        if (!Number.isInteger(n) || n < 0 || n > MAX_DURATION_DAYS || a.isSummary) return
        if (n === 0 && !a.isMilestone) job = upd({ isMilestone: true })
        else if (n > 0 && a.isMilestone) job = upd({ isMilestone: false, durationDays: n })
        else if (n !== a.durationDays) job = upd({ durationDays: n })
        break
      }
      case 'start':
        if (!isIsoDate(v) || a.isSummary) return
        job = upd({ constraintType: 'SNET', constraintDate: v })
        break
      case 'finish':
        if (!isIsoDate(v) || a.isSummary) return
        job = upd({ constraintType: 'FNET', constraintDate: v })
        break
      case 'percent': {
        const n = Number(v)
        if (!Number.isInteger(n) || n < 0 || n > 100 || a.isSummary || n === a.percentComplete) return
        job = () => setProgress({ variables: { scheduleId, patches: [{ id, percentComplete: n }] } }) as unknown as Promise<{ data: any }>
        break
      }
      default:
        return
    }
    if (!job) return
    setPending((m) => { const n = new Map(m); n.set(id, { column, value: v }); return n })
    try {
      await run(job, (d: any) => d.updateScheduleActivities ?? d.setScheduleActivityProgress ?? null)
    } catch {
      /* toast shown by run; cell reverts since nothing was applied to the cache */
    } finally {
      setPending((m) => { const n = new Map(m); n.delete(id); return n })
    }
  }, [byId, updateActivities, setProgress, run, scheduleId])

  // `commitPredecessors` implements spec B §9.4's diff → delete → update → create sequence. The
  // editor already ran `parsePredecessors` for its synchronous validation; re-running it here
  // (rather than threading `LocalLink[]` through the commit callback) keeps `onCommit`'s signature
  // identical to every other cell's (`(v: string, move) => void`).
  const commitPredecessors = useCallback(async (id: string, raw: string) => {
    const r = parsePredecessors(raw, tree, id)
    if (!r.ok) return
    const current = predecessorsByActivity.get(id)?.local ?? []
    const { create, update, remove } = diffPredecessors(current, r.links)
    if (!create.length && !update.length && !remove.length) return
    setPending((m) => { const n = new Map(m); n.set(id, { column: 'predecessors', value: raw.trim() }); return n })
    try {
      for (const depId of remove) {
        await run(
          () => deleteDependency({ variables: { scheduleId, id: depId } }) as unknown as Promise<{ data: any }>,
          (d: any) => d.deleteScheduleDependency,
        )
      }
      for (const l of update) {
        await run(
          () => updateDependency({ variables: { scheduleId, id: l.id, type: l.type, lagDays: l.lagDays } }) as unknown as Promise<{ data: any }>,
          (d: any) => d.updateScheduleDependency,
        )
      }
      for (const l of create) {
        await run(
          () => createDependency({ variables: { scheduleId, input: { predecessorId: l.predecessorId, successorId: id, type: l.type, lagDays: l.lagDays } } }) as unknown as Promise<{ data: any }>,
          (d: any) => d.createScheduleDependency,
        )
      }
    } catch {
      /* run toasted; remaining steps are skipped; the cell shows whatever the successful steps applied to the cache */
    } finally {
      setPending((m) => { const n = new Map(m); n.delete(id); return n })
    }
  }, [tree, predecessorsByActivity, run, deleteDependency, updateDependency, createDependency, scheduleId])

  const onCommitCell = useCallback((id: string, column: ColumnId, raw: string, move: CellMove) => {
    if (move === 'insert') {
      // Task 3: close the editor, enqueue the update on the serial `run` queue, THEN request the
      // new row — so the mutation queue sees update before create.
      api.getState().setEditingCell(null)
      if (column === 'predecessors') commitPredecessors(id, raw)
      else commitCell(id, column, raw)
      insertAfter?.(id)
      return
    }
    moveFocusAfterCommit(id, column, move)
    if (column === 'predecessors') commitPredecessors(id, raw)
    else commitCell(id, column, raw)
  }, [moveFocusAfterCommit, commitCell, commitPredecessors, insertAfter, api])
  const onCancelCell = useCallback(() => { api.getState().setEditingCell(null) }, [api])

  return { editingCell, pending, startEdit, startEditEdge, onCommitCell, onCancelCell }
}
